import { RailReveal } from "@/components/rail-kit/RailReveal";
import { leafName } from "@/dnd";
import type { DraggableData } from "@/dnd/ids";
import { buildFilesRailDragPayload } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/buildFilesRailDragPayload";
import { FilesRailDropTarget } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailDropTarget";
import type { FilesRailFileMenuActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailFileRow";
import type { FilesRailOsDropHandler } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/useFilesRailWorkspaceFiles";
import { RailDisclosureIcon } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailChevron";
import {
  RailRowMenu,
  railRowMenuProps,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowMenu";
import { useRailExpansion } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailExpansion";
import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import {
  ObjectActionMenu,
  ObjectActionMenuCloseProvider,
  useAttachFileRecordTransferSubmenus,
  useObjectActions,
  type ObjectActionHandlers,
  type ObjectKind,
} from "@/object-actions";
import { DERIVED_FOLDER_ID_PREFIX } from "@/pages/workspaces/utils/folderPaths";
import { getFileRecordName } from "@/panes/file-record-detail/contentType";
import { cn } from "@/utils";
import type { FileRecord } from "@cohesive/api-client";
import { useDraggable } from "@dnd-kit/core";
import { Folder, FolderOpen } from "lucide-react";
import { useMemo } from "react";

/** Expandable folder row — draggable + drop target for moves and OS uploads. */
export function FilesRailFolderRow({
  record,
  workspaceId,
  onOsDrop,
  menuActions,
  onUploadToPath,
  onCreateDocumentAtPath,
  onCreateSubfolder,
  children,
}: {
  record: FileRecord;
  workspaceId: string;
  onOsDrop?: FilesRailOsDropHandler | null;
  /** Rename / Move / Delete handlers (folders reuse the file actions). */
  menuActions?: FilesRailFileMenuActions;
  /** Upload files into this folder's path. */
  onUploadToPath?: (parentPath: string) => void;
  /** Create a blank document inside this folder's path. */
  onCreateDocumentAtPath?: (parentPath: string) => void;
  /** Create a subfolder inside this folder's path. */
  onCreateSubfolder?: (parentPath: string) => void;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useRailExpansion(
    `file-folder:${workspaceId}:${record.path}`,
  );
  const menu = useRailRowContextMenu();
  const label = getFileRecordName(record);
  // Derived prefix folders (no backing row) render and accept drops but
  // offer no menu — there is nothing to rename/move/archive.
  const showMenu =
    !!menuActions && !record.id.startsWith(DERIVED_FOLDER_ID_PREFIX);

  const folderKind = useMemo<ObjectKind>(
    () => ({ type: "file-record", class: record.class, isFolder: true }),
    [record.class],
  );

  const menuHandlers = useMemo<ObjectActionHandlers>(
    () => ({
      // In the rail, opening a folder expands it in place (no separate view).
      open: () => setOpen(true),
      rename: menuActions?.onRename
        ? () => window.setTimeout(() => menuActions.onRename!(record), 0)
        : undefined,
      // Move and Copy to are submenu-only — the destination picker attached
      // below is the interaction; the handlers exist so the resolver doesn't
      // drop the actions before the submenu attaches.
      move: () => {},
      "copy-to": () => {},
      delete: menuActions?.onDelete
        ? () => window.setTimeout(() => menuActions.onDelete!(record), 0)
        : undefined,
      "upload-files": onUploadToPath
        ? () => onUploadToPath(record.path)
        : undefined,
      "new-document": onCreateDocumentAtPath
        ? () => onCreateDocumentAtPath(record.path)
        : undefined,
      "new-folder": onCreateSubfolder
        ? () => onCreateSubfolder(record.path)
        : undefined,
    }),
    [
      menuActions,
      onCreateDocumentAtPath,
      onCreateSubfolder,
      onUploadToPath,
      record,
    ],
  );

  const { actions: baseActions } = useObjectActions({
    kind: folderKind,
    surface: "left-rail",
    scope: "canonical",
    enabled: menu.open && showMenu,
    state: {
      canUploadFiles: true,
      canEdit: record.can_edit !== false,
      canDelete: !!menuActions?.onDelete,
    },
    handlers: menuHandlers,
  });
  // Move to / Copy to open the nested destination picker; the dialog handler
  // stays behind as the fallback for hosts that can't cascade.
  const actions = useAttachFileRecordTransferSubmenus(baseActions, record);

  const draggableData: DraggableData = useMemo(
    () => ({
      kind: "files",
      payloads: [buildFilesRailDragPayload(record, workspaceId)],
      primaryName: leafName(record.path),
    }),
    [record, workspaceId],
  );

  const draggable = useDraggable({
    id: `rail-folder-row:${workspaceId}:${record.id}`,
    data: draggableData,
  });

  const setRowRef = (el: HTMLButtonElement | null) => {
    draggable.setNodeRef(el);
  };

  return (
    <FilesRailDropTarget
      workspaceId={workspaceId}
      parentPath={record.path}
      onOsDrop={onOsDrop}
    >
      <div className="flex flex-col">
        <div
          onContextMenu={showMenu ? menu.onContextMenu : undefined}
          className={cn(
            "group/row relative flex min-h-7 w-full items-center gap-2.5 rounded-md px-2 hover:bg-n-05",
            showMenu && "pr-8",
            draggable.isDragging && "cursor-grabbing opacity-40",
          )}
        >
          <button
            ref={setRowRef}
            type="button"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            data-state={open ? "open" : "closed"}
            className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
            {...draggable.attributes}
            {...draggable.listeners}
          >
            <RailDisclosureIcon open={open} icon={open ? FolderOpen : Folder} />
            <span className="flex-1 truncate text-base text-n-80">{label}</span>
          </button>

          {showMenu && (
            <RailRowMenu {...railRowMenuProps(menu)} label={label}>
              {/* Submenu commits (click a workspace/folder row) bypass Radix's
                  Item-select auto-close, so the picker dismisses the menu
                  through this context. */}
              <ObjectActionMenuCloseProvider value={() => menu.setOpen(false)}>
                <ObjectActionMenu
                  actions={actions}
                  kind={folderKind}
                  name={label}
                />
              </ObjectActionMenuCloseProvider>
            </RailRowMenu>
          )}
        </div>
        {/* pl-2: the rail's uniform 8px step per level, matching the
            workspace→sections indent. Child rows (subfolders' chevrons,
            files' phantom spacers) all carry the same internal 28px icon
            offset, so every level's icons land exactly 8px deeper than
            the parent's. */}
        <RailReveal open={open} contentClassName="flex flex-col pl-2">
          {children}
        </RailReveal>
      </div>
    </FilesRailDropTarget>
  );
}
